import type { PhaseContext } from "../../core/lifecycle/kernel";
import { createTriggerDefinitionInstaller } from "../triggers/index.ts";
import type { DefinitionHandle as TriggerDefinitionHandle } from "../triggers/index.ts";
import { commitTransfer as commitTransferS03 } from "../transactions/index";
import type { CommitResult, LedgerState, LeaseBook, TransferCommand } from "../transactions/index";
import { reducePhase8, reducePhase9, type ReducerDefinition } from "./reducer";
import type {
  DefinitionInstallBatch,
  DefinitionInstallResult,
  Phase8Input,
  Phase9Input,
  TriggerActionFixtureHandlerArgs,
  TriggerActionHandlerBundle,
  TriggerActionRuntime,
  TriggerActionRuntimeDependencies,
} from "./types";
import { assertRecord } from "./validation";

const DEPENDENCY_KEYS = ["validateD017Manifest", "canonicalizeD017Manifest", "sha256Bytes", "validateNavLayout"] as const;
const FIXTURE_KEYS = [
  "selectSlice", "replaceSlice", "selectActorSamples", "selectDefinitionHandles", "selectOpportunities",
  "selectPhase7ArrivalObservations", "selectPreauthorizedGrants", "selectActiveAuthorities",
  "selectPurchaseEligibility", "selectLedgerState", "selectLeaseBook", "commitTransfer",
] as const;

const EMPTY_HANDLES = Object.freeze([]);
const DEPENDENCY_FAILURE = Object.freeze({ status: "dependency-failure", handles: EMPTY_HANDLES }) as DefinitionInstallResult;
const ALREADY_INSTALLED = Object.freeze({ status: "already-installed", handles: EMPTY_HANDLES }) as DefinitionInstallResult;

function validDependencies(value: unknown): value is TriggerActionRuntimeDependencies {
  try {
    assertRecord(value, DEPENDENCY_KEYS, "TriggerActionRuntimeDependencies");
    return DEPENDENCY_KEYS.every((key) => typeof value[key] === "function");
  } catch { return false; }
}

function assertFixtureArgs<Root>(value: TriggerActionFixtureHandlerArgs<Root>): void {
  assertRecord(value, FIXTURE_KEYS, "TriggerActionFixtureHandlerArgs");
  for (const key of FIXTURE_KEYS) if (typeof value[key] !== "function") throw new TypeError(`${key} must be callable`);
}

export function createTriggerActionRuntime(dependencies: TriggerActionRuntimeDependencies): TriggerActionRuntime {
  const dependenciesValid = validDependencies(dependencies);
  const installer = dependenciesValid ? createTriggerDefinitionInstaller(dependencies) : null;
  let installAttempted = false;
  let installationSucceeded = false;

  const installDefinitions = async (batch: DefinitionInstallBatch): Promise<DefinitionInstallResult> => {
    if (installAttempted) return ALREADY_INSTALLED;
    installAttempted = true;
    if (installer === null) return DEPENDENCY_FAILURE;
    const result = await installer.installDefinitions(batch);
    installationSucceeded = result.status === "installed";
    return result;
  };
  const assertInstallationSucceeded = (): void => {
    if (installer === null) throw new TypeError("runtime dependencies are invalid");
    if (!installationSucceeded) throw new TypeError("runtime definitions are not installed");
  };
  const resolve = (handles: readonly object[]): readonly ReducerDefinition[] => {
    return installer!.resolveDefinitions(handles as readonly TriggerDefinitionHandle[]) as unknown as readonly ReducerDefinition[];
  };
  const phase8Reducer = (input: Phase8Input) => {
    assertInstallationSucceeded();
    return reducePhase8(input, resolve);
  };
  const phase9Reducer = (input: Phase9Input) => {
    assertInstallationSucceeded();
    return reducePhase9(input, commitTransferS03);
  };

  const createFixtureHandlers = <Root>(args: TriggerActionFixtureHandlerArgs<Root>): TriggerActionHandlerBundle<Root> => {
    assertFixtureArgs(args);
    const phase8 = (root: Readonly<Root>, context: Readonly<PhaseContext>): Root => {
      const result = phase8Reducer({
        authorityTick: context.tick,
        previousSlice: args.selectSlice(root),
        actorSamples: args.selectActorSamples(root, context.tick),
        definitionHandles: args.selectDefinitionHandles(root, context.tick),
        opportunities: args.selectOpportunities(root, context.tick),
        phase7ArrivalObservations: args.selectPhase7ArrivalObservations(root, context.tick),
        preauthorizedActionGrants: args.selectPreauthorizedGrants(root, context.tick),
        activeActionAuthorities: args.selectActiveAuthorities(root, context.tick),
        purchaseEligibilitySnapshots: args.selectPurchaseEligibility(root, context.tick),
      });
      return args.replaceSlice(root, result.slice);
    };
    const phase9 = (root: Readonly<Root>, context: Readonly<PhaseContext>): Root => {
      assertInstallationSucceeded();
      const result = reducePhase9({
        authorityTick: context.tick,
        phase8Slice: args.selectSlice(root),
        ledgerState: args.selectLedgerState(root),
        leaseBook: args.selectLeaseBook(root),
      }, args.commitTransfer as (state: LedgerState, command: TransferCommand, leases: LeaseBook | null, tick: number) => CommitResult);
      return args.replaceSlice(root, result.slice);
    };
    return Object.freeze({ phase8, phase9 });
  };

  return Object.freeze({
    installDefinitions,
    reducePhase8: phase8Reducer,
    reducePhase9: phase9Reducer,
    createFixtureHandlers,
  });
}
